import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  List, ListItem, ListItemIcon, ListItemText, Divider, Paper
} from '@mui/material';
import {
  CloudUpload, Download, CheckCircle, Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

function BulkUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        setResult({ success: false, message: 'Please select an Excel file (.xlsx or .xls)' });
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_URL}/content/template/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'JBDE_Content_Upload_Template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading template:', error);
      setResult({ success: false, message: 'Error downloading template' });
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setResult({ success: false, message: 'Please select a file first' });
      return;
    }

    setUploading(true);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/content/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult({
        success: response.data.success,
        message: response.data.message,
        count: response.data.data?.count,
        errors: response.data.errors || [] // Include errors from partial success
      });
      
      // Only reset file if all rows succeeded
      if (response.data.success && (!response.data.errors || response.data.errors.length === 0)) {
        setFile(null);
        document.getElementById('file-input').value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      // Extract errors - handle both array and object formats
      let errorList = [];
      if (error.response?.data?.errors) {
        if (Array.isArray(error.response.data.errors)) {
          errorList = error.response.data.errors;
        } else if (typeof error.response.data.errors === 'object') {
          errorList = Object.values(error.response.data.errors);
        }
      }
      
      // If no structured errors, try to extract from message
      if (errorList.length === 0 && error.response?.data) {
        console.log('Full error response data:', JSON.stringify(error.response.data, null, 2));
      }
      
      setResult({
        success: false,
        message: error.response?.data?.message || 'Error uploading file',
        errors: errorList,
        rawError: error.response?.data // Store raw error for debugging
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} mb={3}>
        📊 Bulk Upload Content
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            How to Use Bulk Upload
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Step 1: Download Template"
                secondary="Download the Excel template with all required fields and sample data"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Step 2: Fill Data"
                secondary="Add your content data. Use the reference sheets for valid dropdown values"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <InfoIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Step 3: Upload File"
                secondary="Upload the completed Excel file to create multiple content entries at once"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600} mb={3}>
            Download Template
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadTemplate}
            size="large"
          >
            Download Excel Template
          </Button>
          <Typography variant="body2" color="text.secondary" mt={2}>
            The template includes sample data and reference sheets for all dropdown fields
          </Typography>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight={600} mb={3}>
            Upload Completed File
          </Typography>

          <Paper
            sx={{
              p: 3,
              border: '2px dashed #ccc',
              borderRadius: 2,
              textAlign: 'center',
              backgroundColor: '#fafafa',
              mb: 3
            }}
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUpload />}
                size="large"
              >
                Choose Excel File
              </Button>
            </label>
            {file && (
              <Typography variant="body1" mt={2} color="primary">
                Selected: {file.name}
              </Typography>
            )}
          </Paper>

          {uploading && (
            <Box mb={3}>
              <Typography variant="body2" mb={1}>Uploading...</Typography>
              <LinearProgress />
            </Box>
          )}

          {result && (
            <Alert 
              severity={result.success ? 'success' : 'error'} 
              sx={{ mb: 3 }}
              icon={result.success ? <CheckCircle /> : <ErrorIcon />}
            >
              <Typography variant="body1" fontWeight={600}>
                {result.message}
              </Typography>
              {result.count && (
                <Typography variant="body2">
                  {result.count} content items were created successfully
                </Typography>
              )}
              {result.errors && result.errors.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    Please fix the following errors:
                  </Typography>
                  {result.errors.slice(0, 10).map((err, idx) => (
                    <Typography key={idx} variant="body2" sx={{ 
                      fontFamily: 'monospace', 
                      fontSize: '0.875rem',
                      lineHeight: 1.6
                    }}>
                      • {typeof err === 'string' ? err : JSON.stringify(err)}
                    </Typography>
                  ))}
                  {result.errors.length > 10 && (
                    <Typography variant="body2" fontWeight={600} mt={1}>
                      ...and {result.errors.length - 10} more errors
                    </Typography>
                  )}
                </Box>
              )}
              {!result.success && (!result.errors || result.errors.length === 0) && result.rawError && (
                <Box mt={2} sx={{ 
                  p: 2, 
                  bgcolor: '#fff3cd', 
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  <Typography variant="caption" fontWeight={600} display="block" mb={1}>
                    Raw Error Response:
                  </Typography>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                    {JSON.stringify(result.rawError, null, 2)}
                  </pre>
                </Box>
              )}
              {!result.success && (!result.errors || result.errors.length === 0) && !result.rawError && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Check browser console (F12) for detailed error information
                </Typography>
              )}
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              startIcon={<CloudUpload />}
              onClick={handleUpload}
              disabled={!file || uploading}
              size="large"
            >
              Upload File
            </Button>
            {result?.success && (
              <Button
                variant="outlined"
                onClick={() => navigate('/content')}
              >
                View Content Library
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default BulkUpload;
